import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setTenantHeader, setUnauthorizedHandler } from "@/lib/api";
import { cacheClear } from "@/lib/cache";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Losing access mid-session — suspended or deleted — should end the session
  // there and then, not on the next refresh.
  useEffect(() => {
    setUnauthorizedHandler((message) => {
      setUser((current) => {
        if (current) {
          cacheClear();
          setTenantHeader(null);
          try {
            window.localStorage.removeItem("active_tenant");
          } catch {
            /* private browsing */
          }
          if (message) {
            try {
              window.sessionStorage.setItem("signed_out_reason", message);
            } catch {
              /* not critical */
            }
          }
        }
        return false;
      });
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* the cookie is cleared either way */
    }
    // Nothing cached should survive into the next person's session.
    cacheClear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
